/**
 * FT-1459 → FT-1484 step 3: THE LIVE ROLE OVERLAY — every library role's
 * latest version, laid over the script that uses it, on the storyteller's
 * client; the town takes the storyteller's.
 *
 * Two kinds of role follow the library:
 *
 *   OFFICIAL characters — keyed by `officialId`. FT-1485: THE LIBRARY IS
 *     THEIR ONE HOME. Every official is a row in the role library, owned by
 *     the site owner's account (server/experiences/botc/routes.ts, the roles
 *     section), and `GET /official` answers every one of them, edited or
 *     not — read in full once per page load, then only "saved since".
 *     roles.json is ONLY the complete fallback when that can't be reached,
 *     and the provider of the fields no row stores (the bundled edition a
 *     character is printed in, its night-order position, `remindersGlobal`,
 *     `automation`). This module writes the row's `name`, `ability`, team,
 *     `reminders`, `setup`, whether it wakes each night, the borrowed art
 *     `icon` → `imageAlt`, and the night (words, steps, night-sheet
 *     reminders, the spent token) ONTO the bundled role objects themselves.
 *     In place on
 *     purpose: roles.json is one module instance, so every reader — the
 *     store's `rolesJSONbyId`, `getRolesByEdition`, `setCustomRoles`, the
 *     hover card, the night lab — sees the house text with no second lookup
 *     to forget. Night ORDER is never overlaid; that belongs to the script.
 *   CUSTOM characters — keyed by their library id (`golemRoleId`, which
 *     roles.js toAppRole stamps on every role that came from the library).
 *     A script carries them as snapshots; `GET /batch?ids=` answers their
 *     latest rows and the store's `liveRolesApplied` lays each onto the
 *     loaded character in place. A custom role with no library id (an
 *     imported script's own) has nothing to follow and is left alone.
 *
 * WHEN (the user's rules, FT-1484): every save goes live at once, but A GAME
 * LOCKS ITS ROLE VERSIONS AT THE DEAL. The storyteller's client refreshes on
 * page load, whenever a script lands (a script set, opened or re-opened, a
 * town joined as its storyteller) and at Play again — and NEVER while its
 * game is dealt (socket.js's `_isDealt` signal: the deal pulse or the host's
 * per-town deal stash). A fetch that comes back after a deal is dropped.
 *
 * WHO: the storyteller only. A player's characters come in the storyteller's
 * edition frame (socket.js) — the custom roles as today, and now the house
 * overlay too (`houseFrame` / `applyHouseFrame`), so every chair reads the
 * storyteller's locked versions. A player never fetches on its own.
 *
 * Plus, for the Studio: OWNERSHIP of the officials — "may this signed-in
 * account edit them?" with ONE request per account per session, and the row
 * an owner saves in place (`officialRow`, one request per id, cached).
 */
import Vue from "vue";
import rolesJSON from "../roles.json";
// FT-1475: the house's night WORDS land on the copy layer's house map.
import { registerHouseCopy } from "./nightSigns";
// FT-1475 part 2: …and its STEPS (and "wakes while dead") on the night
// schema's house layer, over the shipped entry.
import { registerHouseNight } from "./nightInfo";
// FT-1484 step 3: a library row → the script's custom-role shape.
import { liveFieldsDiffer, teamFromRoleType, toAppRole } from "./roles";
// FT-1484 step 3: the host's per-town deal stash — half of the lock.
import { dealTimeFor } from "./stats";

const API = "/api/botc/roles";

/** FT-1484 step 3: the batch read's own cap (routes.ts ROLE_BATCH_MAX). */
const BATCH_MAX = 100;

/** The bundled officials by id — the SAME objects the store maps. */
const base = new Map(rolesJSON.map((role) => [role.id, role]));

/** FT-1485: the teams a row may give an official (app spelling). */
const TEAMS = new Set(["townsfolk", "outsider", "minion", "demon", "traveler"]);

/** FT-1484 step 3: each official's own text as shipped — what a character
 *  goes back to when the overlay stops covering it. Taken at import, before
 *  anything here has written a word. */
const stock = new Map(
  rolesJSON.map((role) => [
    role.id,
    {
      name: role.name,
      ability: role.ability,
      reminders: Array.isArray(role.reminders) ? role.reminders.slice() : [],
      setup: role.setup,
      // FT-1485: the team and the night positions are the rows' now too
      team: role.team,
      firstNight: role.firstNight,
      otherNight: role.otherNight,
    },
  ]),
);

/** FT-1475: the art each official wears as bundled (none carry an imageAlt
 *  today) — what a cleared house `icon` falls back to. */
const baseAlt = new Map(rolesJSON.map((role) => [role.id, role.imageAlt]));

/** FT-1475: the night-sheet reminders each official ships with — what a
 *  cleared house reminder falls back to. */
const baseReminders = new Map(
  rolesJSON.map((role) => [
    role.id,
    { first: role.firstNightReminder, other: role.otherNightReminder },
  ]),
);

/** FT-1475: one reminder key onto the bundled object — the house's text
 *  where it wrote one, the shipped text otherwise. */
function setReminder(role, key, text, shipped) {
  if (text) Vue.set(role, key, text);
  else if (shipped !== undefined) Vue.set(role, key, shipped);
  else if (key in role) Vue.delete(role, key);
}

/** Reactive, so the pens light up the moment the probe answers. */
export const house = Vue.observable({ accountId: null, owns: false });

/** officialId → Promise<row | null>, for the account in `rowsAccount`. */
const rows = new Map();
let rowsAccount = null;
let probe = null;

/** A different account (or signing out) forgets everything it was told. */
function resetFor(accountId) {
  if (rowsAccount === accountId) return;
  rows.clear();
  probe = null;
  rowsAccount = accountId;
  house.accountId = accountId;
  house.owns = false;
}

/**
 * The library row that IS official character `officialId`, as THIS session
 * sees it (`ownedByYou` included), or null. One request per id per account;
 * a failure is not cached, so a later ask can try again.
 */
export function officialRow(officialId, accountId) {
  resetFor(accountId || null);
  if (!rows.has(officialId)) {
    const pending = fetch(`${API}/official/${encodeURIComponent(officialId)}`, {
      credentials: "same-origin",
    })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((row) => {
        if (!row && rows.get(officialId) === pending) rows.delete(officialId);
        return row;
      });
    rows.set(officialId, pending);
  }
  return rows.get(officialId);
}

/**
 * Does this account own the house? Asked ONCE per account per session, of
 * the first official the caller has in view (`sampleId`). Signed out resolves
 * false with no request. The per-id truth is still checked when a pen is
 * actually used (`officialRow`), so a wrong guess can only ever hide a pen.
 */
export function probeHouse(accountId, sampleId) {
  resetFor(accountId || null);
  if (!accountId || !sampleId) return Promise.resolve(false);
  if (!probe) {
    probe = officialRow(sampleId, accountId).then((row) => {
      if (rowsAccount !== accountId) return false;
      if (!row) probe = null; // unreachable — let a later view ask again
      house.owns = !!(row && row.ownedByYou);
      return house.owns;
    });
  }
  return probe;
}

// ─── THE OFFICIALS ───────────────────────────────────────────────────────────

/** Lay one override (or a saved row) onto its bundled character. */
function overlayOne(o) {
  const role = o && base.get(o.officialId);
  if (!role) return false;
  if (typeof o.name === "string" && o.name.trim()) role.name = o.name.trim();
  if (typeof o.ability === "string" && o.ability.trim()) {
    role.ability = o.ability.trim();
  }
  if (Array.isArray(o.reminders)) role.reminders = o.reminders.slice();
  if (typeof o.setup === "boolean") role.setup = o.setup;
  // FT-1485: the TEAM is the row's (houseEntry already spelled it the app's
  // way, "traveler").
  if (TEAMS.has(o.team)) role.team = o.team;
  // FT-1485: WHETHER it wakes is the row's; WHERE is the script's night
  // order, so the shipped position stands (the row's whole-number seed can't
  // hold 43.33). A row that says it sleeps takes it off that night; one that
  // says it wakes keeps the shipped slot. Always over a reset character.
  if (o.wakesFirst === false) role.firstNight = 0;
  if (o.wakesOther === false) role.otherNight = 0;
  // FT-1475: the house ART. `icon` names the official whose engraving this
  // character wears — its own id (or none) means its own art. Only a bundled
  // id can land: Token `require`s `imageAlt`, so an unknown one would throw.
  // Vue.set/delete so a render already reading the object notices.
  if (typeof o.icon === "string") {
    const icon = o.icon.trim();
    const alt = baseAlt.get(o.officialId);
    if (icon && icon !== o.officialId && base.has(icon)) {
      Vue.set(role, "imageAlt", icon);
    } else if (alt) {
      Vue.set(role, "imageAlt", alt);
    } else if ("imageAlt" in role) {
      Vue.delete(role, "imageAlt");
    }
  }
  // FT-1475: the house WORDS — `night.copy` onto the copy layer, where every
  // night sentence resolves (script override → these → the shipped text).
  // A row without a `night` object leaves the standing words alone; one with
  // a `night` but no copy clears them.
  if (o.night && typeof o.night === "object" && !Array.isArray(o.night)) {
    registerHouseCopy(o.officialId, o.night.copy);
    // FT-1475 part 2: the rest of the house's night —
    //   steps / wakesWhenDead → the night schema's house layer (absent steps
    //     leave the shipped entry exactly as it is);
    //   reminders → the role's own firstNightReminder / otherNightReminder,
    //     the text every checklist surface already reads as the official
    //     reminder (a cleared one restores the shipped text);
    //   spentToken → offers an "Ability spent" token in the reminder menu.
    registerHouseNight(o.officialId, o.night);
    const rem = o.night.reminders || {};
    const shipped = baseReminders.get(o.officialId) || {};
    setReminder(role, "firstNightReminder", rem.first, shipped.first);
    setReminder(role, "otherNightReminder", rem.other, shipped.other);
    Vue.set(role, "golemSpentToken", o.night.spentToken === true);
  }
  // FT-1491: the character's SECTIONS — the row's whole list onto the
  // bundled object as golemSections (the key every reader takes); none
  // clears. Official is just a tag: the same key a custom character carries.
  if (Array.isArray(o.sections)) {
    if (o.sections.length) {
      Vue.set(
        role,
        "golemSections",
        o.sections.map((s) => ({ ...s })),
      );
    } else if ("golemSections" in role) {
      Vue.delete(role, "golemSections");
    }
  }
  return true;
}

/** FT-1484 step 3: one official back to exactly its shipped self — text,
 *  art, night words, steps, reminders, spent token. */
function resetOne(officialId) {
  const role = base.get(officialId);
  const shipped = stock.get(officialId);
  if (!role || !shipped) return;
  role.name = shipped.name;
  role.ability = shipped.ability;
  role.reminders = shipped.reminders.slice();
  role.team = shipped.team;
  role.firstNight = shipped.firstNight;
  role.otherNight = shipped.otherNight;
  if (shipped.setup === undefined) Vue.delete(role, "setup");
  else role.setup = shipped.setup;
  const alt = baseAlt.get(officialId);
  if (alt) Vue.set(role, "imageAlt", alt);
  else if ("imageAlt" in role) Vue.delete(role, "imageAlt");
  const rem = baseReminders.get(officialId) || {};
  setReminder(role, "firstNightReminder", "", rem.first);
  setReminder(role, "otherNightReminder", "", rem.other);
  if ("golemSpentToken" in role) Vue.delete(role, "golemSpentToken");
  // FT-1491: roles.json ships no sections
  if ("golemSections" in role) Vue.delete(role, "golemSections");
  registerHouseCopy(officialId, null);
  registerHouseNight(officialId, null);
}

/** FT-1484 step 3: an override reduced to what the overlay reads — the
 *  shape the edition frame carries (never an id beyond `officialId`).
 *  FT-1485: a library row speaks `roleType` ("traveller"); an entry already
 *  in this shape (the edition frame) speaks `team`. */
function houseEntry(o) {
  return {
    officialId: o.officialId,
    name: o.name,
    ability: o.ability,
    team: o.team || (o.roleType ? teamFromRoleType(o.roleType) : undefined),
    reminders: o.reminders,
    setup: o.setup,
    wakesFirst: typeof o.wakesFirst === "boolean" ? o.wakesFirst : undefined,
    wakesOther: typeof o.wakesOther === "boolean" ? o.wakesOther : undefined,
    icon: o.icon,
    night: o.night === undefined ? null : o.night,
    // FT-1491: the row's sections (the frame strips the storyteller's)
    sections: Array.isArray(o.sections) ? o.sections : undefined,
    version: o.version,
  };
}

/**
 * FT-1485: does this entry make its character differ from roles.json as
 * shipped? Every official now has an entry (the library is their home), but a
 * player's client starts from roles.json, so the edition frame need only
 * carry the ones that differ — the rest reset to exactly what it has.
 */
function differsFromStock(o) {
  const s = stock.get(o.officialId);
  if (!s) return false;
  // FT-1491: roles.json ships no sections, so any section differs
  if (Array.isArray(o.sections) && o.sections.length) return true;
  const text = (v) => (typeof v === "string" ? v.trim() : "");
  if (text(o.name) && text(o.name) !== s.name) return true;
  if (text(o.ability) && text(o.ability) !== s.ability) return true;
  if (TEAMS.has(o.team) && o.team !== s.team) return true;
  if (
    Array.isArray(o.reminders) &&
    JSON.stringify(o.reminders) !== JSON.stringify(s.reminders)
  ) {
    return true;
  }
  if (typeof o.setup === "boolean" && o.setup !== !!s.setup) return true;
  if (o.wakesFirst === false && s.firstNight > 0) return true;
  if (o.wakesOther === false && s.otherNight > 0) return true;
  const icon = text(o.icon);
  if (icon && icon !== o.officialId && base.has(icon)) return true;
  const n = o.night;
  if (n && typeof n === "object") {
    if (n.copy && Object.keys(n.copy).length) return true;
    if (Array.isArray(n.steps) && n.steps.length) return true;
    if (n.spentToken === true || typeof n.wakesWhenDead === "boolean") {
      return true;
    }
    const rem = n.reminders || {};
    const shipped = baseReminders.get(o.officialId) || {};
    if (text(rem.first) && text(rem.first) !== text(shipped.first)) return true;
    if (text(rem.other) && text(rem.other) !== text(shipped.other)) return true;
  }
  return false;
}

/** officialId → the house entry standing on this client right now. */
const applied = new Map();

/** The standing overlay's fingerprint, so an identical refresh is a no-op. */
function signature(map) {
  return JSON.stringify(
    [...map.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
}

/**
 * FT-1484 step 3: REPLACE the standing house overlay with `list`. Every
 * character the new list no longer covers goes back to its shipped text; an
 * identical list changes nothing (and commits nothing). Returns whether
 * anything moved.
 */
function setHouse(list, store) {
  const next = new Map();
  (Array.isArray(list) ? list : []).forEach((o) => {
    if (o && base.has(o.officialId)) next.set(o.officialId, houseEntry(o));
  });
  if (signature(next) === signature(applied)) return false;
  new Set([...applied.keys(), ...next.keys()]).forEach(resetOne);
  applied.clear();
  next.forEach((entry, id) => {
    overlayOne(entry);
    applied.set(id, entry);
  });
  if (store) store.commit("houseRolesApplied");
  return true;
}

/** Overlay a list ON TOP of what stands (the owner's own save) and tell the
 *  store its characters changed under it. */
export function applyOverrides(list, store) {
  let n = 0;
  (Array.isArray(list) ? list : []).forEach((o) => {
    if (!o || !base.has(o.officialId)) return;
    const entry = houseEntry(o);
    resetOne(o.officialId);
    overlayOne(entry);
    applied.set(o.officialId, entry);
    n += 1;
  });
  if (n && store) store.commit("houseRolesApplied");
  return n;
}

/**
 * FT-1484 step 3: THE STORYTELLER'S HOUSE, for the edition frame — the
 * overlay standing on this client (so the one locked at the deal), narrowed
 * to the characters this game can reach: the script's, and the travellers
 * that can join any game. FT-1485: and to the ones that differ from roles.json
 * as shipped — a player's client already holds the rest exactly.
 */
export function houseFrame(state) {
  return [...applied.values()].filter(
    (o) =>
      ((state.roles && state.roles.has(o.officialId)) ||
        (state.otherTravelers && state.otherTravelers.has(o.officialId))) &&
      differsFromStock(o),
  );
}

/** FT-1484 step 3: a player took the storyteller's house from the frame —
 *  from now on nothing this client fetched on its own may replace it. */
let frameLocked = false;

/**
 * FT-1484 step 3: a PLAYER's side of the frame — the storyteller's house
 * overlay replaces whatever stood here, so every chair reads the storyteller's
 * locked versions of the officials.
 */
export function applyHouseFrame(list, store) {
  frameLocked = true;
  setHouse(list, store);
}

// ─── THE CUSTOM ROLES ────────────────────────────────────────────────────────

/** The distinct library ids of the script's custom characters (the Fabled
 *  included — a custom Fabled lives in `fabled`, not `roles`). */
function scriptLibraryIds(state) {
  const ids = new Set();
  const add = (role) => {
    if (role && role.isCustom && role.golemRoleId) ids.add(role.golemRoleId);
  };
  state.roles.forEach(add);
  state.fabled.forEach(add);
  return [...ids];
}

/** FT-1484 step 3: the latest rows for `ids`, BATCH_MAX a request. Throws on
 *  a failed request (the caller keeps what stands). */
export async function fetchLatestRoles(ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const chunk = ids.slice(i, i + BATCH_MAX).map(encodeURIComponent);
    const res = await fetch(`${API}/batch?ids=${chunk.join(",")}`, {
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`batch failed (${res.status})`);
    const { roles } = await res.json();
    if (Array.isArray(roles)) out.push(...roles);
  }
  return out;
}

/**
 * FT-1484 step 3: library rows onto the loaded script, by library id. Only
 * characters whose library fields actually moved are committed; returns how
 * many.
 */
export function applyLibraryRows(list, store) {
  const loaded = new Map();
  const collect = (role) => {
    if (role && role.isCustom && role.golemRoleId) {
      loaded.set(role.golemRoleId, role);
    }
  };
  store.state.roles.forEach(collect);
  store.state.fabled.forEach(collect);
  const fresh = new Map();
  (Array.isArray(list) ? list : []).forEach((row) => {
    const role = row && loaded.get(row.id);
    if (!role) return;
    const next = toAppRole(row);
    if (liveFieldsDiffer(role, next)) fresh.set(row.id, next);
  });
  if (fresh.size) store.commit("liveRolesApplied", fresh);
  return fresh.size;
}

// ─── THE REFRESH, AND THE LOCK ───────────────────────────────────────────────

/** socket.js `_isDealt`, read from the store: the deal pulse or the host's
 *  per-town deal stash. */
function isDealt(state) {
  const { session } = state;
  return !!session.isRolesDistributed || !!dealTimeFor(session.sessionId || "");
}

/** May THIS client take the library's latest now? The storyteller, between
 *  games. `boot` = a page load, which the user's rule lets refresh. */
function mayRefresh(state, boot) {
  if (state.session.isSpectator) return false;
  return boot || !isDealt(state);
}

// ─── THE OFFICIALS' HOME (FT-1485) ───────────────────────────────────────────

/** officialId → the library row that IS that character, as last read. */
const officialRows = new Map();

/**
 * Which library rows ARE official characters. The script builder leaves them
 * out of its library lists — the official entry is that same character.
 * Reactive, so a list already on screen re-filters when the read lands.
 */
export const officials = Vue.observable({ rowIds: [] });

/** FT-1486: the library row that IS official `officialId`, from the one
 *  officials read already made — no request — or null before it lands. */
export function cachedOfficialRow(officialId) {
  return officialRows.get(officialId) || null;
}

/** The newest save time read so far; the next read asks only for rows saved
 *  after it. */
let watermark = "";
/** Has this page load had its one full read? (A failed one is not counted,
 *  so the next trigger reads in full again.) */
let fullyRead = false;

/**
 * THE OFFICIALS, FROM THEIR ONE HOME. The first read of a page load takes
 * every official row (`GET /official`); every later one asks only for the
 * rows saved since the newest it has seen (`?since=`), merged over what it
 * holds. Resolves to the whole standing list; throws on a failed request.
 */
async function fetchOfficials() {
  const since =
    fullyRead && watermark ? `?since=${encodeURIComponent(watermark)}` : "";
  const res = await fetch(`${API}/official${since}`, {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`officials failed (${res.status})`);
  const { roles } = await res.json();
  (Array.isArray(roles) ? roles : []).forEach((row) => {
    if (!row || !row.officialId) return;
    officialRows.set(row.officialId, row);
    if (typeof row.updatedAt === "string" && row.updatedAt > watermark) {
      watermark = row.updatedAt;
    }
  });
  fullyRead = true;
  const ids = [...officialRows.values()].map((row) => row.id);
  if (ids.join() !== officials.rowIds.join()) officials.rowIds = ids;
  return [...officialRows.values()];
}

/**
 * FT-1484 step 3: take every library role this script uses at its latest —
 * the officials' house edits and the custom roles' library rows — unless the
 * game is dealt. Checked twice: before asking, and again when the answers
 * land (a deal while the fetch was out wins). Quiet: a failure keeps what
 * stands. Resolves to whether anything changed.
 */
export async function refreshLiveRoles(store, { boot = false } = {}) {
  if (!mayRefresh(store.state, boot)) return false;
  const ids = scriptLibraryIds(store.state);
  const [officialList, libraryRows] = await Promise.all([
    fetchOfficials().catch(() => null),
    ids.length ? fetchLatestRoles(ids).catch(() => null) : [],
  ]);
  if (!mayRefresh(store.state, boot)) return false;
  frameLocked = false;
  // FT-1485: an unreachable library keeps what stands — roles.json as
  // shipped, on a page that never reached it.
  const houseMoved = officialList ? setHouse(officialList, store) : false;
  const customs = libraryRows ? applyLibraryRows(libraryRows, store) : 0;
  return houseMoved || customs > 0;
}

let timer = null;
let running = null;
let again = false;

/** One refresh at a time; a trigger during one queues exactly one more. */
function run(store, opts) {
  if (running) {
    again = true;
    return running;
  }
  running = refreshLiveRoles(store, opts)
    .catch(() => false)
    .finally(() => {
      running = null;
      if (again) {
        again = false;
        run(store);
      }
    });
  return running;
}

/** Triggers in one tick (setCustomRoles then setEdition) share one refresh —
 *  and a Play again's own deal-stash clear, later in the same handler, has
 *  landed before it asks. */
function schedule(store) {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    run(store);
  }, 0);
}

/** The commits that mean "a script just landed, or the next game is
 *  starting" on this client. */
const TRIGGERS = new Set([
  "setCustomRoles",
  "setEdition",
  "clearEnded",
  "session/setSessionId",
]);

/**
 * FT-1484 step 3: wire the live overlay to the store — the page-load refresh
 * (the storyteller's; a player waits for the storyteller's frame), and a
 * refresh on every trigger above plus the script editor opening. The lock
 * is `refreshLiveRoles`' own, so a trigger mid-game simply does nothing.
 */
export function bindLiveRoles(store) {
  store.subscribe(({ type, payload }) => {
    if (
      TRIGGERS.has(type) ||
      (type === "toggleModal" && payload === "edition")
    ) {
      schedule(store);
    }
  });
  if (!store.state.session.isSpectator) run(store, { boot: true });
}

/**
 * Boot, the FT-1459 name: the house for a page that has no store binding.
 * A client in a town as a player never takes it — its town's frame decides.
 */
export async function initHouseRoles(store) {
  try {
    if (store && store.state.session.isSpectator) return;
    const officialList = await fetchOfficials();
    if (!frameLocked) setHouse(officialList, store);
  } catch {
    // no platform in reach — the printed text stands
  }
}

/**
 * The owner just saved `row` in place: overlay it here without a reload, and
 * refresh the cached row so the next pen opens the saved text. The
 * storyteller's own save of their own game's character — it applies at once,
 * as it always has, and the edition frame carries it to the town.
 */
export function applySavedRow(row, store) {
  if (!row) return;
  if (row.officialId) {
    rows.set(row.officialId, Promise.resolve(row));
    officialRows.set(row.officialId, row);
    applyOverrides([{ ...row, officialId: row.officialId }], store);
    return;
  }
  // FT-1484 step 3: a custom role — onto the loaded script by library id
  if (store) applyLibraryRows([row], store);
}
